import { useEffect, useState } from "react";
import { Loader2, History, RefreshCw, X, ScanLine, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../api";

const roleLabel = { owner: "Owner/Pemilik", admin_operasional: "Admin Operasional", kasir: "Kasir/Keuangan", supir: "Supir" };
const roleBadge = {
  owner: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  admin_operasional: "bg-blue-100 text-blue-800 border border-blue-200",
  kasir: "bg-amber-100 text-amber-800 border border-amber-200",
  supir: "bg-violet-100 text-violet-800 border border-violet-200",
};
const aksiBadge = (aksi) => {
  if (aksi.startsWith("Hapus")) return "bg-red-100 text-red-800 border border-red-200";
  if (aksi.startsWith("Buat") || aksi.startsWith("Catat")) return "bg-emerald-100 text-emerald-800 border border-emerald-200";
  return "bg-zinc-100 text-zinc-700 border border-zinc-200";
};
const fmtTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const ScanUsageCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/scan-usage").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data || data.rows.length === 0) return null;
  return (
    <div data-testid="scan-usage-card" className="bg-white border border-zinc-200 rounded-lg shadow-sm">
      <div className="px-5 pt-4">
        <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
          <ScanLine className="w-4 h-4 text-emerald-700" /> Pemakaian Scan Slip AI
        </h2>
        <p className="text-xs text-zinc-500">Jumlah scan per pengguna (batas saat ini: {data.limit_harian} scan/hari) — tiap scan memakai saldo Universal Key</p>
      </div>
      <div className="px-5 py-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="scan-usage-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              {["Pengguna", "Hari Ini", "7 Hari", "30 Hari", "Total"].map((h) => (
                <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.email} data-testid={`scan-usage-row-${r.email}`} className="border-b border-zinc-100">
                <td className="px-4 py-2.5 whitespace-nowrap font-medium text-zinc-900">{r.email}</td>
                <td className={`px-4 py-2.5 tabular-nums ${r.hari_ini >= data.limit_harian ? "text-red-700 font-semibold" : ""}`}>{r.hari_ini}</td>
                <td className="px-4 py-2.5 tabular-nums">{r.hari_7}</td>
                <td className="px-4 py-2.5 tabular-nums">{r.hari_30}</td>
                <td className="px-4 py-2.5 tabular-nums font-medium">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const fmtWaktu = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "-" : d.toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const ScanGagalCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/scan-gagal").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data || data.rows.length === 0) return null;
  return (
    <div data-testid="scan-gagal-card" className="bg-white border border-red-200 rounded-lg shadow-sm">
      <div className="px-5 pt-4">
        <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" /> Supir Gagal Scan Slip
        </h2>
        <p className="text-xs text-zinc-500">Kegagalan scan per supir (foto tidak terbaca / kena batas harian). Owner menerima WA saat mencapai {data.ambang}x gagal dalam sehari.</p>
      </div>
      <div className="px-5 py-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="scan-gagal-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              {["Supir", "Hari Ini", "7 Hari", "30 Hari", "Alasan Terakhir", "Terakhir", "WA Owner"].map((h) => (
                <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.email} data-testid={`scan-gagal-row-${r.email}`} className="border-b border-zinc-100">
                <td className="px-4 py-2.5 whitespace-nowrap font-medium text-zinc-900">{r.nama}<span className="block text-xs font-normal text-zinc-400">{r.email}</span></td>
                <td className={`px-4 py-2.5 tabular-nums ${r.hari_ini >= data.ambang ? "text-red-700 font-semibold" : ""}`}>{r.hari_ini}</td>
                <td className="px-4 py-2.5 tabular-nums">{r.hari_7}</td>
                <td className="px-4 py-2.5 tabular-nums">{r.hari_30}</td>
                <td className="px-4 py-2.5 text-zinc-600 max-w-[260px] truncate" title={r.alasan_terakhir || "-"}>{r.alasan_terakhir || "-"}</td>
                <td className="px-4 py-2.5 whitespace-nowrap text-zinc-600">{fmtWaktu(r.waktu_terakhir)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  {r.notified_hari_ini ? (
                    <Badge className="bg-red-100 text-red-800 border border-red-200 font-medium">Terkirim</Badge>
                  ) : (
                    <span className="text-xs text-zinc-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function AuditPage() {
  const [logs, setLogs] = useState(null);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("semua");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = () => {
    const params = {};
    if (userFilter !== "semua") params.user_email = userFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return api.get("/audit", { params }).then(({ data }) => {
      setLogs(data.items);
      setUsers(data.users);
    }).catch(() => setLogs([]));
  };
  useEffect(() => { load(); }, [userFilter, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasFilter = userFilter !== "semua" || dateFrom !== "" || dateTo !== "";

  return (
    <div data-testid="audit-page" className="space-y-6 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
            <History className="w-7 h-7 text-emerald-700" /> Log Aktivitas User
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Jejak audit: siapa membuat/mengubah trip, pengeluaran uang jalan, dan pencairan</p>
        </div>
        <Button data-testid="audit-refresh-button" variant="outline" size="sm" onClick={load} className="border-zinc-300">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Muat Ulang
        </Button>
      </div>

      <ScanGagalCard />
      <ScanUsageCard />

      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger data-testid="audit-filter-user" className="bg-white sm:w-64"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="semua">Semua User</SelectItem>
            {users.map((u) => <SelectItem key={u} value={u} data-testid={`audit-user-option-${u}`}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input data-testid="audit-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-white sm:w-40" title="Dari tanggal" />
          <span className="text-xs text-zinc-400">s/d</span>
          <Input data-testid="audit-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-white sm:w-40" title="Sampai tanggal" />
        </div>
        {hasFilter && (
          <Button data-testid="audit-reset-filters" variant="outline" size="sm" onClick={() => { setUserFilter("semua"); setDateFrom(""); setDateTo(""); }} className="border-zinc-300 shrink-0">
            <X className="w-4 h-4 mr-1.5" /> Reset
          </Button>
        )}
        {logs !== null && <span data-testid="audit-count" className="text-xs text-zinc-500 sm:ml-auto tabular-nums">{logs.length} aktivitas</span>}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden">
        {logs === null ? (
          <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : logs.length === 0 ? (
          <p data-testid="audit-empty" className="px-5 py-14 text-center text-sm text-zinc-400">
            {hasFilter ? "Tidak ada aktivitas yang cocok dengan filter." : "Belum ada aktivitas tercatat. Perubahan trip, pengeluaran, dan pencairan akan muncul di sini."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["Waktu", "User", "Role", "Aksi", "Modul", "Referensi", "Detail"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                    <td className="px-4 py-2.5 whitespace-nowrap text-zinc-600">{fmtTime(l.waktu)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap font-medium text-zinc-900">{l.user_name}<span className="block text-xs font-normal text-zinc-400">{l.user_email}</span></td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <Badge className={`${roleBadge[l.role] || ""} font-medium`}>{roleLabel[l.role] || l.role}</Badge>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <Badge className={`${aksiBadge(l.aksi)} font-medium`}>{l.aksi}</Badge>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{l.modul}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap font-medium">{l.ref}</td>
                    <td className="px-4 py-2.5 text-zinc-600 max-w-[320px]">{l.detail || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
